// Parser that turns normalized .luis source into a lightweight AST.
// It also expands object variable declarations while collecting rules.
import type { LuisArrayVariables, LuisNode, LuisNodeType, LuisObjectVariables } from './types'
import { createNode, currentNode, isStandaloneObjectVarCall, pushDeclarations } from './parser/utils'

// Parse source into nested selector/at-rule nodes for later rendering.
export function parseLuis(
  source: string,
  objectVariables: LuisObjectVariables,
  startLine = 1,
  arrayVariables: LuisArrayVariables = {},
): LuisNode {
  // Root node keeps top-level children and never renders directly.
  const root = createNode('root', '', startLine)
  const stack: LuisNode[] = [root]
  const blockStartLines: number[] = [startLine]
  let buffer = ''
  let line = startLine
  // Scan source one char at a time to build nested blocks safely.
  for (let i = 0; i < source.length; i += 1) {
    const char = source[i]

    // Open a new block when we hit '{' after a selector token.
    if (char === '{') {
      const rawToken = buffer.trim()
      buffer = ''

      // Ignore empty tokens like accidental whitespace before '{'.
      if (!rawToken) {
        continue
      }

      const nodeType: LuisNodeType = rawToken.startsWith('@') ? 'at-rule' : 'selector'
      const node = createNode(nodeType, rawToken, line)

      currentNode(stack).children.push(node)
      stack.push(node)
      blockStartLines.push(line)
      continue
    }

    // Finalize one declaration when we reach ';'.
    if (char === ';') {
      const rawDeclaration = buffer.trim()
      buffer = ''

      // Skip empty declarations created by repeated separators.
      if (!rawDeclaration) {
        continue
      }

      const activeNode = currentNode(stack)
      // Root node does not store declarations directly.
      if (activeNode.type === 'root') {
        // Preserve top-level at-rule declarations such as `@import ...;`.
        if (rawDeclaration.startsWith('@')) {
          activeNode.declarations.push(rawDeclaration)
        } else {
          // Ignore non-at-rule root declarations.
        }
        continue
      }

      pushDeclarations(activeNode, rawDeclaration, objectVariables, arrayVariables)
      continue
    }

    // Close current block and flush trailing declaration text.
    if (char === '}') {
      const trailing = buffer.trim()
      buffer = ''

      // Only flush trailing text when there is content to persist.
      if (trailing.length > 0) {
        const activeNode = currentNode(stack)
        // Ignore trailing root text that is not part of a rule block.
        if (activeNode.type !== 'root') {
          pushDeclarations(activeNode, trailing, objectVariables, arrayVariables)
        }
      }

      // Guard against unmatched closing braces.
      if (stack.length === 1) {
        throw new Error(`Invalid .luis syntax: unmatched closing brace at line ${line}.`)
      }

      stack.pop()
      blockStartLines.pop()
      continue
    }

    // Handle shorthand object-expansion lines without explicit semicolons.
    if (char === '\n' || char === '\r') {
      const pending = buffer.trim()
      const activeNode = currentNode(stack)

      // Detect `var(--name)` object-expansion shorthand lines.
      if (activeNode.type !== 'root' && isStandaloneObjectVarCall(pending)) {
        pushDeclarations(activeNode, pending, objectVariables, arrayVariables)
        buffer = ''
        continue
      }
    }

    buffer += char

    // Track line number so syntax errors can point to source location.
    if (char === '\n') {
      line += 1
    }
  }

  // Ensure every opened block has a matching closing brace.
  if (stack.length !== 1) {
    const missingLine = blockStartLines[blockStartLines.length - 1] ?? line
    throw new Error(`Invalid .luis syntax: missing closing brace for block opened near line ${missingLine}.`)
  }

  // Return root AST node.
  return root
}
